// frontend/src/api/client.js
// SignalIQ API Client — production-safe auth, route normalization, stable error handling,
// Render cold-start protection, and conservative performance controls.
//
// Keeps existing api.get/post/put/patch/delete interface.
// Adds:
// - Auth/login timeout protection for Render cold starts
// - Duplicate concurrent GET request de-duplication
// - Request timeout protection
// - Optional GET cache, disabled by default
// - Stable 401 cleanup after SECRET_KEY rotation
// - No endpoint contract changes

const DEFAULT_LOCAL_API_BASE = "http://127.0.0.1:8000";
const TOKEN_KEY = "signaliq_token";

const AUTH_STORAGE_KEYS = [
  TOKEN_KEY,
  "token",
  "access_token",
  "refresh_token",
  "nxa_token",
  "signaliq_access_token",
  "signaliq_refresh_token",
  "signaliq_user",
  "user",
  "auth",
  "auth_user",
  "current_user",
  "session",
];

const AUTH_STORAGE_PREFIXES = [
  "auth.",
  "auth_",
  "signaliq.auth",
  "signaliq_auth",
  "nxa_auth",
];

// Production timeout policy.
// Render/backend cold starts can exceed 30 seconds, especially on login after idle.
const DEFAULT_REQUEST_TIMEOUT_MS = 90000;
const AUTH_REQUEST_TIMEOUT_MS = 120000;
const FAST_DASHBOARD_TIMEOUT_MS = 9000;
const LONG_RUNNING_ANALYTICS_TIMEOUT_MS = 120000;
const AI_ANALYTICS_TIMEOUT_MS = 180000;

const DEFAULT_GET_CACHE_TTL_MS = 0;
const STALE_CACHE_MAX_AGE_MS = 1000 * 60 * 15;

const inflightGetRequests = new Map();
const responseCache = new Map();

function cleanBaseUrl(value) {
  return String(value || "").trim().replace(/\/+$/, "");
}

const API_BASE_URL = cleanBaseUrl(
  import.meta.env.VITE_API_BASE_URL ||
    import.meta.env.VITE_API_URL ||
    DEFAULT_LOCAL_API_BASE
);

const BASE = `${API_BASE_URL}/api/v1`.replace(/\/+$/, "");

let inMemoryToken = null;

// ─────────────────────────────────────────────────────────────────────────────
// TOKEN HELPERS
// ─────────────────────────────────────────────────────────────────────────────

export function setToken(token) {
  const cleanToken = String(token || "").trim();
  inMemoryToken = cleanToken || null;

  try {
    if (cleanToken) {
      localStorage.setItem(TOKEN_KEY, cleanToken);
    } else {
      localStorage.removeItem(TOKEN_KEY);
    }
  } catch {
    // Storage may be unavailable in private/restricted mode.
  }
}

export function getToken() {
  if (inMemoryToken) return inMemoryToken;

  try {
    const stored = String(localStorage.getItem(TOKEN_KEY) || "").trim();
    if (stored) {
      inMemoryToken = stored;
      return stored;
    }
  } catch {
    // Ignore storage issues.
  }

  return "";
}

export function clearToken() {
  inMemoryToken = null;

  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    // Ignore storage issues.
  }
}

export function clearAuthSession() {
  inMemoryToken = null;

  const shouldRemoveKey = (key) => {
    const normalized = String(key || "").trim();
    if (!normalized) return false;
    if (AUTH_STORAGE_KEYS.includes(normalized)) return true;
    return AUTH_STORAGE_PREFIXES.some((prefix) => normalized.startsWith(prefix));
  };

  try {
    AUTH_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));

    for (let i = localStorage.length - 1; i >= 0; i -= 1) {
      const key = localStorage.key(i);
      if (shouldRemoveKey(key)) localStorage.removeItem(key);
    }
  } catch {
    // Ignore storage issues.
  }

  try {
    AUTH_STORAGE_KEYS.forEach((key) => sessionStorage.removeItem(key));

    for (let i = sessionStorage.length - 1; i >= 0; i -= 1) {
      const key = sessionStorage.key(i);
      if (shouldRemoveKey(key)) sessionStorage.removeItem(key);
    }
  } catch {
    // Ignore storage issues.
  }

  try {
    document.cookie.split(";").forEach((cookie) => {
      const name = cookie.split("=")[0]?.trim();
      if (!name || !shouldRemoveKey(name)) return;
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
    });
  } catch {
    // Ignore cookie issues.
  }

  clearApiResponseCache();
}

export function getApiBaseUrl() {
  return BASE;
}

// ─────────────────────────────────────────────────────────────────────────────
// URL + ROUTE NORMALIZATION
// ─────────────────────────────────────────────────────────────────────────────

function encodeQueryValue(value) {
  return encodeURIComponent(String(value ?? ""));
}

function appendQuery(path, params = {}) {
  const entries = Object.entries(params).filter(([, value]) => {
    return value !== undefined && value !== null && value !== "";
  });

  if (!entries.length) return path;

  const joiner = path.includes("?") ? "&" : "?";
  const query = entries
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeQueryValue(value)}`)
    .join("&");

  return `${path}${joiner}${query}`;
}

function stripApiPrefix(path = "") {
  let next = String(path || "").trim();
  next = next.replace(/^\/+/, "");

  if (next.startsWith("api/v1/")) {
    next = next.slice("api/v1/".length);
  }

  return `/${next}`.replace(/\/{2,}/g, "/");
}

function normalizeLegacyRoute(method, path, body) {
  if (!path) return { method, path, body };

  if (/^https?:\/\//i.test(path)) {
    return { method, path, body };
  }

  let normalizedPath = stripApiPrefix(path);
  let normalizedMethod = String(method || "GET").toUpperCase();
  let normalizedBody = body;

  const fhiMatch = normalizedPath.match(/^\/fhi\/(\d+)\/([^/?#]+)([?#].*)?$/i);
  if (fhiMatch) {
    const [, companyId, period, existingSuffix = ""] = fhiMatch;
    const suffix = existingSuffix || "";
    const periodValue = decodeURIComponent(period);

    const [queryPart, hashPart] = suffix.split("#");
    const query = queryPart || "";
    const hash = hashPart ? `#${hashPart}` : "";

    normalizedPath =
      appendQuery(`/fhi/${companyId}${query}`, { period: periodValue }) + hash;
  }

  if (
    normalizedMethod === "GET" &&
    /^\/reports\/prepare\/?([?#].*)?$/i.test(normalizedPath)
  ) {
    normalizedMethod = "POST";
    normalizedBody = normalizedBody ?? {};
  }

  return {
    method: normalizedMethod,
    path: normalizedPath,
    body: normalizedBody,
  };
}

function buildUrl(path = "") {
  if (!path) return BASE;

  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  return `${BASE}${stripApiPrefix(path)}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// RESPONSE + ERROR HANDLING
// ─────────────────────────────────────────────────────────────────────────────

async function parseResponse(response) {
  if (response.status === 204) return null;

  const contentType = response.headers.get("content-type") || "";
  const isJson = contentType.includes("application/json");

  try {
    return isJson ? await response.json() : await response.text();
  } catch {
    return null;
  }
}

function extractErrorMessage(payload, fallback = "Request failed") {
  if (!payload) return fallback;

  if (typeof payload === "string" && payload.trim()) {
    return payload;
  }

  if (typeof payload.detail === "string" && payload.detail.trim()) {
    return payload.detail;
  }

  if (
    typeof payload.detail?.message === "string" &&
    payload.detail.message.trim()
  ) {
    return payload.detail.message;
  }

  if (typeof payload.error === "string" && payload.error.trim()) {
    return payload.error;
  }

  if (typeof payload.message === "string" && payload.message.trim()) {
    return payload.message;
  }

  try {
    return JSON.stringify(payload.detail || payload);
  } catch {
    return fallback;
  }
}

function createApiError({ message, response, path, method, payload }) {
  return Object.assign(new Error(message), {
    name: "SignalIQApiError",
    status: response?.status,
    statusText: response?.statusText,
    method,
    path,
    url: response?.url,
    payload,
  });
}

export function getApiError(error, fallback = "Request failed") {
  if (!error) return fallback;
  if (typeof error === "string") return error;

  if (typeof error.message === "string" && error.message.trim()) {
    return error.message;
  }

  return extractErrorMessage(error.payload, fallback);
}

function logApiError(error) {
  if (typeof console === "undefined") return;

  const status = error?.status;
  const method = error?.method || "REQUEST";
  const path = error?.path || "unknown path";
  const payload = error?.payload;

  const compactPayload =
    typeof payload === "string"
      ? payload.slice(0, 1200)
      : payload && typeof payload === "object"
        ? JSON.stringify(payload).slice(0, 1200)
        : payload;

  console.error("[SignalIQ API]", {
    status,
    method,
    path,
    message: error?.message,
    payload: compactPayload,
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// PERFORMANCE HELPERS
// ─────────────────────────────────────────────────────────────────────────────

function cacheKeyFor(method, path, body) {
  const m = String(method || "GET").toUpperCase();

  if (m === "GET") {
    return `${m}:${path}`;
  }

  try {
    return `${m}:${path}:${JSON.stringify(body || {})}`;
  } catch {
    return `${m}:${path}`;
  }
}

function getCachedResponse(key, { allowStale = false } = {}) {
  const cached = responseCache.get(key);
  if (!cached) return undefined;

  const now = Date.now();
  const expired = now > cached.expiresAt;
  const staleTooOld = now > cached.staleUntil;

  if (expired && (!allowStale || staleTooOld)) {
    responseCache.delete(key);
    return undefined;
  }

  return {
    value: cached.value,
    stale: expired,
    cachedAt: cached.cachedAt,
  };
}

function setCachedResponse(key, value, ttlMs) {
  if (!ttlMs || ttlMs <= 0) return;

  const now = Date.now();

  responseCache.set(key, {
    value,
    cachedAt: now,
    expiresAt: now + ttlMs,
    staleUntil: now + ttlMs + STALE_CACHE_MAX_AGE_MS,
  });
}

function isAuthRoute(path = "") {
  return /\/auth\/(login|me|register|refresh|password|reset|forgot|verify|logout)/i.test(
    String(path || "")
  );
}

function isLoginOrRefreshRoute(path = "") {
  return /\/auth\/(login|refresh)/i.test(String(path || ""));
}

function isPublicBrowserRoute() {
  if (typeof window === "undefined") return false;

  const pathname = window.location.pathname || "/";
  return [
    "/login",
    "/register",
    "/privacy",
    "/terms",
    "/email-verified",
  ].includes(pathname);
}

function isLongRunningAnalyticsRoute(path = "") {
  const value = String(path || "");
  return (
    /^\/bsp(?:\/|\?|$)/i.test(value) ||
    /^\/fhi(?:\/|\?|$)/i.test(value) ||
    /^\/benchmarks(?:\/|\?|$)/i.test(value) ||
    /^\/forecasting(?:\/|\?|$)/i.test(value) ||
    /^\/sensitivity(?:\/|\?|$)/i.test(value) ||
    /^\/scenario(?:\/|\?|$)/i.test(value) ||
    /^\/variance(?:\/|\?|$)/i.test(value) ||
    /^\/unit-economics(?:\/|\?|$)/i.test(value) ||
    /^\/macro(?:\/|\?|$)/i.test(value) ||
    /^\/reports(?:\/|\?|$)/i.test(value)
  );
}

function isAiAnalyticsRoute(path = "") {
  const value = String(path || "");
  return (
    /^\/ai-insights(?:\/|\?|$)/i.test(value) ||
    /^\/aurelia(?:\/|\?|$)/i.test(value) ||
    /^\/ai(?:\/|\?|$)/i.test(value)
  );
}

function isFastDashboardRequest(opts = {}) {
  const headers = opts.headers || {};

  return (
    opts.fast === true ||
    opts.lightweight === true ||
    headers["X-SignalIQ-Mode"] === "fast" ||
    headers["x-signaliq-mode"] === "fast"
  );
}

function isBenchmarkRoute(path = "") {
  return /^\/benchmarks(?:\/|\?|$)/i.test(String(path || ""));
}

function buildRequestOptionsForRoute(path, opts = {}) {
  if (opts.signal) return opts;

  const suppliedTimeout =
    opts.timeout !== undefined && opts.timeout !== null && Number.isFinite(Number(opts.timeout))
      ? Number(opts.timeout)
      : null;

  let routeTimeout = DEFAULT_REQUEST_TIMEOUT_MS;

  // Important: auth/login must not use fast dashboard timeout.
  // Login is allowed to wait longer because Render can cold-start slowly.
  if (isAuthRoute(path)) {
    routeTimeout = AUTH_REQUEST_TIMEOUT_MS;
  } else if (isAiAnalyticsRoute(path)) {
    routeTimeout = AI_ANALYTICS_TIMEOUT_MS;
  } else if (isLongRunningAnalyticsRoute(path)) {
    routeTimeout = LONG_RUNNING_ANALYTICS_TIMEOUT_MS;
  } else if (isFastDashboardRequest(opts)) {
    routeTimeout = FAST_DASHBOARD_TIMEOUT_MS;
  }

  // Benchmarks must never inherit a 1.8s dashboard timeout.
  // DB-first benchmark loads are analytical requests, and live FMP enrichment
  // needs enough time to call peer discovery + ratios/key-metrics safely.
  if (isBenchmarkRoute(path)) {
    const benchmarkTimeout = LONG_RUNNING_ANALYTICS_TIMEOUT_MS;
    return {
      ...opts,
      timeout: suppliedTimeout === null ? benchmarkTimeout : Math.max(suppliedTimeout, benchmarkTimeout),
      fast: false,
      lightweight: false,
    };
  }

  // For other long-running analytics routes, never downgrade them to a tiny
  // caller-supplied timeout. For normal routes, preserve caller intent.
  if (isLongRunningAnalyticsRoute(path) || isAiAnalyticsRoute(path) || isAuthRoute(path)) {
    return {
      ...opts,
      timeout: suppliedTimeout === null ? routeTimeout : Math.max(suppliedTimeout, routeTimeout),
    };
  }

  return {
    ...opts,
    timeout: suppliedTimeout === null ? routeTimeout : suppliedTimeout,
  };
}

function withTimeoutSignal(opts = {}) {
  if (opts.signal) {
    return {
      signal: opts.signal,
      cleanup() {},
    };
  }

  const timeoutMs = Number.isFinite(opts.timeout)
    ? Number(opts.timeout)
    : DEFAULT_REQUEST_TIMEOUT_MS;

  if (
    !timeoutMs ||
    timeoutMs <= 0 ||
    typeof AbortController === "undefined"
  ) {
    return {
      signal: undefined,
      cleanup() {},
    };
  }

  const controller = new AbortController();

  const timer = setTimeout(() => {
    controller.abort();
  }, timeoutMs);

  return {
    signal: controller.signal,
    cleanup() {
      clearTimeout(timer);
    },
  };
}

function shouldDeduplicateGet(method, opts = {}) {
  return (
    String(method || "GET").toUpperCase() === "GET" &&
    opts.dedupe !== false
  );
}

function createTimeoutError({ path, method, opts }) {
  const timeoutMs = Number.isFinite(opts?.timeout)
    ? Number(opts.timeout)
    : DEFAULT_REQUEST_TIMEOUT_MS;

  const authTimeout = isAuthRoute(path);

  return createApiError({
    message: authTimeout
      ? "Login is taking longer than expected because the backend may still be waking up. Please wait a moment and try again."
      : isFastDashboardRequest(opts)
        ? "This dashboard request is taking longer than expected. SignalIQ kept the page responsive; refresh or retry after the backend finishes warming up."
        : "The request took too long to finish. Please retry after the backend finishes preparing the analysis.",
    response: null,
    path,
    method,
    payload: {
      reason: "frontend_timeout",
      timeout_ms: timeoutMs,
      auth_request: authTimeout,
      fast_dashboard_request: isFastDashboardRequest(opts),
    },
  });
}

function redirectToLogin(reason = "session_expired") {
  if (typeof window === "undefined") return;
  if (isPublicBrowserRoute()) return;

  const current = `${window.location.pathname || "/"}${window.location.search || ""}`;
  const next = current && current !== "/login" ? `&next=${encodeURIComponent(current)}` : "";
  const target = `/login?reason=${encodeURIComponent(reason)}${next}`;

  try {
    window.dispatchEvent(
      new CustomEvent("signaliq:auth-invalid", {
        detail: { reason, next: current },
      })
    );
  } catch {
    // Ignore event issues.
  }

  window.location.replace(target);
}

function handleUnauthorized({ path, opts, payload }) {
  const message = extractErrorMessage(
    payload,
    "Your session is invalid or expired. Please sign in again."
  );

  if (opts.clearTokenOn401 !== false) {
    clearAuthSession();
  }

  const shouldRedirect =
    opts.redirectOn401 !== false &&
    !isAuthRoute(path) &&
    !isPublicBrowserRoute();

  if (shouldRedirect) {
    redirectToLogin("session_expired");
  }

  return message;
}

// ─────────────────────────────────────────────────────────────────────────────
// CORE REQUEST
// ─────────────────────────────────────────────────────────────────────────────

async function request(method, path, body = null, opts = {}) {
  const normalized = normalizeLegacyRoute(method, path, body);
  const normalizedMethod = normalized.method;
  const normalizedPath = normalized.path;
  const normalizedBody = normalized.body;

  const requestKey = cacheKeyFor(
    normalizedMethod,
    normalizedPath,
    normalizedBody
  );

  const isGet = normalizedMethod.toUpperCase() === "GET";

  const cacheTtlMs = Number.isFinite(opts.cacheTtlMs)
    ? Number(opts.cacheTtlMs)
    : DEFAULT_GET_CACHE_TTL_MS;

  if (isGet && cacheTtlMs > 0) {
    const cached = getCachedResponse(requestKey);
    if (cached !== undefined) return cached.value;
  }

  if (isGet && shouldDeduplicateGet(normalizedMethod, opts)) {
    const inflight = inflightGetRequests.get(requestKey);
    if (inflight) return inflight;
  }

  const execute = async () => {
    const token = getToken();

    const isFormData =
      typeof FormData !== "undefined" && normalizedBody instanceof FormData;

    const headers = {
      Accept: "application/json",
      ...(opts.headers || {}),
    };

    if (!isFormData && !headers["Content-Type"] && !headers["content-type"]) {
      headers["Content-Type"] = "application/json";
    }

    if (token && !headers.Authorization && !headers.authorization) {
      headers.Authorization = `Bearer ${token}`;
    }

    const requestOpts = buildRequestOptionsForRoute(normalizedPath, opts);
    const { signal, cleanup } = withTimeoutSignal(requestOpts);

    const config = {
      method: normalizedMethod,
      headers,
      credentials: opts.credentials || "include",
      ...(signal ? { signal } : {}),
    };

    if (normalizedBody !== null && normalizedBody !== undefined) {
      config.body = isFormData ? normalizedBody : JSON.stringify(normalizedBody);
    }

    const retries = Number.isInteger(opts.retries)
      ? Math.max(0, opts.retries)
      : isLoginOrRefreshRoute(normalizedPath)
        ? 1
        : isGet && !isFastDashboardRequest(opts)
          ? 1
          : 0;

    let lastError = null;

    try {
      for (let attempt = 0; attempt <= retries; attempt += 1) {
        try {
          const response = await fetch(buildUrl(normalizedPath), config);
          const payload = await parseResponse(response);

          if (response.status === 401) {
            const message = handleUnauthorized({
              path: normalizedPath,
              opts,
              payload,
            });

            throw createApiError({
              message,
              response,
              path: normalizedPath,
              method: normalizedMethod,
              payload,
            });
          }

          if (!response.ok) {
            const fallback =
              response.status === 405
                ? `Method not allowed for ${normalizedMethod} ${normalizedPath}. Check frontend/backend route contract.`
                : response.statusText || "Request failed";

            const message = extractErrorMessage(payload, fallback);

            throw createApiError({
              message,
              response,
              path: normalizedPath,
              method: normalizedMethod,
              payload,
            });
          }

          if (isGet && cacheTtlMs > 0) {
            setCachedResponse(requestKey, payload, cacheTtlMs);
          }

          return payload;
        } catch (error) {
          lastError = error;

          const isAbort = error?.name === "AbortError";
          const isAuth = error?.status === 401;
          const isClientError =
            Number.isFinite(error?.status) &&
            error.status >= 400 &&
            error.status < 500 &&
            error.status !== 408 &&
            error.status !== 429;

          if (isAbort) {
            if (isGet && cacheTtlMs > 0 && opts.allowStaleOnError !== false) {
              const stale = getCachedResponse(requestKey, { allowStale: true });
              if (stale !== undefined) {
                const staleValue = stale.value;

                if (
                  staleValue &&
                  typeof staleValue === "object" &&
                  !Array.isArray(staleValue)
                ) {
                  return {
                    ...staleValue,
                    _stale_cache: true,
                    _cache_error: "frontend_timeout",
                  };
                }

                return staleValue;
              }
            }

            const timeoutError = createTimeoutError({
              path: normalizedPath,
              method: normalizedMethod,
              opts: requestOpts,
            });

            if (opts.logErrors !== false) {
              logApiError(timeoutError);
            }

            throw timeoutError;
          }

          if (opts.logErrors !== false && attempt >= retries) {
            logApiError(error);
          }

          if (isAuth || isClientError || attempt >= retries) {
            throw error;
          }

          await new Promise((resolve) =>
            setTimeout(resolve, 600 * (attempt + 1))
          );
        }
      }

      throw lastError || new Error("Request failed");
    } finally {
      cleanup();
    }
  };

  const promise = execute();

  if (isGet && shouldDeduplicateGet(normalizedMethod, opts)) {
    inflightGetRequests.set(requestKey, promise);

    promise
      .finally(() => {
        inflightGetRequests.delete(requestKey);
      })
      .catch(() => {});
  }

  return promise;
}

// ─────────────────────────────────────────────────────────────────────────────
// ENDPOINT HELPERS
// ─────────────────────────────────────────────────────────────────────────────

function getCompanyIdValue(companyOrId) {
  if (companyOrId == null) return "";

  if (typeof companyOrId === "object") {
    return (
      companyOrId.id ??
      companyOrId.company_id ??
      companyOrId.companyId ??
      companyOrId.pk ??
      ""
    );
  }

  return companyOrId;
}

function companyPath(path, companyOrId, params = {}) {
  const companyId = getCompanyIdValue(companyOrId);
  return appendQuery(`${path}/${encodeURIComponent(companyId)}`, params);
}

export function clearApiResponseCache() {
  responseCache.clear();
  inflightGetRequests.clear();
}

export const endpoints = {
  fhi(companyOrId, period = "latest") {
    return companyPath("/fhi", companyOrId, { period });
  },

  fhiHistory(companyOrId) {
    return companyPath("/fhi", companyOrId) + "/history";
  },

  bsp(companyOrId, period = "latest") {
    return companyPath("/bsp", companyOrId, { period });
  },

  bspHistory(companyOrId) {
    return companyPath("/bsp", companyOrId) + "/history";
  },

  benchmarks(companyOrId, params = {}) {
    return companyPath("/benchmarks", companyOrId, params);
  },

  forecasting(companyOrId, params = {}) {
    return companyPath("/forecasting", companyOrId, params);
  },

  forecastingMetric(companyOrId, metric = "revenue", params = {}) {
    return companyPath("/forecasting", companyOrId, {
      metric,
      ...params,
    });
  },

  reportsPrepare() {
    return "/reports/prepare";
  },

  aureliaAnalyze() {
    return "/aurelia/analyze";
  },

  aureliaVoice() {
    return "/aurelia/voice";
  },

  aiAnalyze() {
    return "/ai/analyze";
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// PUBLIC API OBJECT
// ─────────────────────────────────────────────────────────────────────────────

const api = {
  request,

  get(path, opts = {}) {
    return request("GET", path, null, opts);
  },

  post(path, body, opts = {}) {
    return request("POST", path, body, opts);
  },

  put(path, body, opts = {}) {
    return request("PUT", path, body, opts);
  },

  patch(path, body, opts = {}) {
    return request("PATCH", path, body, opts);
  },

  delete(path, opts = {}) {
    return request("DELETE", path, null, opts);
  },

  fhi: {
    get(companyOrId, period = "latest", opts = {}) {
      return request("GET", endpoints.fhi(companyOrId, period), null, opts);
    },

    history(companyOrId, opts = {}) {
      return request("GET", endpoints.fhiHistory(companyOrId), null, opts);
    },
  },

  bsp: {
    get(companyOrId, period = "latest", opts = {}) {
      return request("GET", endpoints.bsp(companyOrId, period), null, opts);
    },

    history(companyOrId, opts = {}) {
      return request("GET", endpoints.bspHistory(companyOrId), null, opts);
    },
  },

  benchmarks: {
    get(companyOrId, params = {}, opts = {}) {
      return request("GET", endpoints.benchmarks(companyOrId, params), null, opts);
    },
  },

  forecasting: {
    get(companyOrId, params = {}, opts = {}) {
      return request(
        "GET",
        endpoints.forecasting(companyOrId, params),
        null,
        opts
      );
    },

    metric(companyOrId, metric = "revenue", params = {}, opts = {}) {
      return request(
        "GET",
        endpoints.forecastingMetric(companyOrId, metric, params),
        null,
        opts
      );
    },
  },

  reports: {
    prepare(body = {}, opts = {}) {
      return request("POST", endpoints.reportsPrepare(), body, opts);
    },
  },

  aurelia: {
    analyze(body = {}, opts = {}) {
      return request("POST", endpoints.aureliaAnalyze(), body, opts);
    },

    voice(body = {}, opts = {}) {
      return request("POST", endpoints.aureliaVoice(), body, opts);
    },

    aiAnalyze(body = {}, opts = {}) {
      return request("POST", endpoints.aiAnalyze(), body, opts);
    },
  },
};

export default api;